import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CAMPOS_ACTIVO,
  CATALOGOS,
  CATALOGO_PADRE,
  CODIGOS_CAMPO_ACTIVO,
  ETIQUETA_CATALOGO,
  type CampoActivo,
  type CodigoCatalogo,
} from '@cmms/shared';
import { api } from '../servicios/api.js';
import { useSesion } from '../servicios/sesion.js';
import {
  AccionesDialogo,
  Aviso,
  Cargando,
  Dialogo,
  DialogoConfirmar,
  Etiqueta,
  Indicador,
  Vacio,
  mensajeError,
  CasillaVerificacion,
  ErroresFormulario,
  SeleccionMultiple,
} from '../componentes/piezas.js';
import { IconoLupa } from '../componentes/iconos.js';
import {
  SelectorCatalogo,
  useCatalogo,
  useTiposActivo,
  useTiposFalla,
  useTiposUbicacion,
  type ItemCatalogo,
  type TipoActivoOpcion,
  type TipoFallaOpcion,
  type TipoUbicacionOpcion,
} from '../componentes/selectores.js';
import { useEsMovil } from '../servicios/responsive.js';

/**
 * Administración de catálogos — §3.2.
 *
 * Son los valores que alimentan los desplegables del resto del producto:
 * fabricantes, modelos, motivos de inactividad, familias de falla. Sin una
 * pantalla propia había que sembrarlos en la base, y un formulario de activo
 * quedaba con la mitad de sus listas vacías y sin manera de completarlas.
 */
type SeccionCatalogo = CodigoCatalogo | 'TIPO_ACTIVO' | 'TIPO_UBICACION' | 'GRUPO_ACTIVO';

const ETIQUETA_SECCION: Record<'TIPO_ACTIVO' | 'TIPO_UBICACION' | 'GRUPO_ACTIVO', string> = {
  GRUPO_ACTIVO: 'Grupos de activos',
  TIPO_ACTIVO: 'Tipos de activo',
  TIPO_UBICACION: 'Tipos de ubicación',
};

export function AdministrarCatalogos(): JSX.Element {
  const { puede } = useSesion();
  const [seccion, setSeccion] = useState<SeccionCatalogo>('FABRICANTE');

  const administra = puede('catalogo:administrar');

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Catálogos</h1>
          <p className="cabecera-subtitulo">
            Valores administrables que alimentan los desplegables del sistema. Mantenerlos limpios es
            lo que permite agrupar por fabricante o por motivo de parada en los reportes.
          </p>
        </div>
      </div>

      {!administra ? (
        <Aviso tono="info">
          Su perfil puede consultar los catálogos pero no modificarlos. La administración requiere el
          permiso de catálogos.
        </Aviso>
      ) : null}

      <div className="editor">
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {seccion === 'TIPO_ACTIVO' ? (
            <PanelTiposActivo administra={puede('activo:escribir')} />
          ) : seccion === 'TIPO_UBICACION' ? (
            <PanelTiposUbicacion administra={puede('ubicacion:escribir')} />
          ) : seccion === 'GRUPO_ACTIVO' ? (
            <PanelGruposActivo administra={puede('activo:escribir')} />
          ) : (
            <PanelCatalogo codigo={seccion} administra={administra} />
          )}
        </div>

        <nav className="panel" aria-label="Catálogos disponibles">
          <div className="panel-cabecera">
            <h2>Catálogos</h2>
          </div>
          <div className="panel-cuerpo" style={{ padding: 8 }}>
            {CATALOGOS.map((c) => (
              <button
                key={c}
                type="button"
                className={`nav-enlace${c === seccion ? ' activo' : ''}`}
                style={{ width: '100%', textAlign: 'left' }}
                onClick={() => setSeccion(c)}
                aria-current={c === seccion}
              >
                <span className="nav-enlace-texto">{ETIQUETA_CATALOGO[c]}</span>
              </button>
            ))}
            {(['TIPO_ACTIVO', 'TIPO_UBICACION', 'GRUPO_ACTIVO'] as const).map((s) => (
              <button
                key={s}
                type="button"
                className={`nav-enlace${s === seccion ? ' activo' : ''}`}
                style={{ width: '100%', textAlign: 'left' }}
                onClick={() => setSeccion(s)}
                aria-current={s === seccion}
              >
                <span className="nav-enlace-texto">{ETIQUETA_SECCION[s]}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
}

function PanelCatalogo({ codigo, administra }: { codigo: CodigoCatalogo; administra: boolean }): JSX.Element {
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [editando, setEditando] = useState<ItemCatalogo | null | undefined>(undefined);
  const [eliminando, setEliminando] = useState<ItemCatalogo | null>(null);

  const { data, isLoading } = useCatalogo(codigo);
  const padre = CATALOGO_PADRE[codigo];

  const refrescar = (): Promise<void> => cliente.invalidateQueries({ queryKey: ['catalogo'] });

  const eliminar = useMutation({
    mutationFn: (id: string) => api.delete(`/catalogos/${id}`),
    onSuccess: async () => {
      await refrescar();
      setEliminando(null);
    },
  });

  const activos = (data ?? []).filter((i) => i.activo).length;

  return (
    <>
      <section className="panel">
        <div className="panel-cabecera">
          <h2>{ETIQUETA_CATALOGO[codigo]}</h2>
          {administra ? (
            <button type="button" className="boton pequeno" onClick={() => setEditando(null)}>
              Nuevo valor
            </button>
          ) : null}
        </div>

        <div className="panel-cuerpo" style={{ paddingBottom: 0 }}>
          <div className="rejilla-indicadores">
            <Indicador titulo="VALORES" valor={data?.length ?? '—'} tono="azul" />
            {/* Antes decía "EN USO", pero cuenta cuántos valores están marcados
                como disponibles para elegir (`activo`), no cuántos están
                efectivamente referenciados por otros registros (§ bug
                reportado: etiqueta engañosa). */}
            <Indicador titulo="DISPONIBLES" valor={activos} tono="verde" />
          </div>
        </div>

        {isLoading ? <Cargando /> : null}
        {data && data.length === 0 ? (
          <Vacio
            titulo={`Sin valores en ${ETIQUETA_CATALOGO[codigo]}`}
            detalle={administra ? 'Agregue el primero con «Nuevo valor».' : undefined}
          />
        ) : null}

        {data && data.length > 0 ? (
          esMovil ? (
            <div className="tarjetas-ot">
              {data.map((i) => (
                <div key={i.id} className="tarjeta-ot" style={i.activo ? undefined : { opacity: 0.55 }}>
                  <div className="tarjeta-ot-cuerpo">
                    <div className="tarjeta-ot-cabecera">
                      <div className="tarjeta-ot-titulo">{i.nombre}</div>
                      {i.activo ? <Etiqueta tono="verde">Activo</Etiqueta> : <Etiqueta tono="gris">Retirado</Etiqueta>}
                    </div>
                    {padre && i.padreNombre ? <div className="tarjeta-ot-nota">{i.padreNombre}</div> : null}
                    {i.descripcion ? <div className="tarjeta-ot-datos"><span>{i.descripcion}</span></div> : null}
                  </div>
                  {administra ? (
                    <div className="acciones-fila">
                      <button type="button" className="boton secundario pequeno" onClick={() => setEditando(i)}>
                        Editar
                      </button>
                      <button type="button" className="boton secundario pequeno" onClick={() => setEliminando(i)}>
                        Eliminar
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    {padre ? <th>{ETIQUETA_CATALOGO[padre]}</th> : null}
                    <th>Descripción</th>
                    <th>Estado</th>
                    {administra ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {data.map((i) => (
                    <tr key={i.id} style={i.activo ? undefined : { opacity: 0.55 }}>
                      <td style={{ fontWeight: 600 }}>{i.nombre}</td>
                      {padre ? <td>{i.padreNombre ?? '—'}</td> : null}
                      <td style={{ color: 'var(--texto-medio)' }}>{i.descripcion ?? '—'}</td>
                      <td>
                        {i.activo ? (
                          <Etiqueta tono="verde">Activo</Etiqueta>
                        ) : (
                          <Etiqueta tono="gris">Retirado</Etiqueta>
                        )}
                      </td>
                      {administra ? (
                        <td>
                          <div className="acciones-fila">
                            <button type="button" className="boton secundario pequeno" onClick={() => setEditando(i)}>
                              Editar
                            </button>
                            <button type="button" className="boton secundario pequeno" onClick={() => setEliminando(i)}>
                              Eliminar
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </section>

      {editando !== undefined ? (
        <FormularioItemCatalogo
          codigo={codigo}
          item={editando}
          alCerrar={() => setEditando(undefined)}
          alGuardar={async () => {
            await refrescar();
            setEditando(undefined);
          }}
        />
      ) : null}

      {eliminando ? (
        <DialogoConfirmar
          titulo="Eliminar valor del catálogo"
          mensaje={`Se eliminará «${eliminando.nombre}». Los registros que ya lo usan conservan la referencia histórica.`}
          cargando={eliminar.isPending}
          error={eliminar.error}
          alCerrar={() => {
            eliminar.reset();
            setEliminando(null);
          }}
          alConfirmar={() => eliminar.mutate(eliminando.id)}
        />
      ) : null}
    </>
  );
}

function FormularioItemCatalogo({
  codigo,
  item,
  alCerrar,
  alGuardar,
}: {
  codigo: CodigoCatalogo;
  item: ItemCatalogo | null;
  alCerrar: () => void;
  alGuardar: () => void | Promise<void>;
}): JSX.Element {
  const [nombre, setNombre] = useState(item?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(item?.descripcion ?? '');
  const [padreId, setPadreId] = useState(item?.padreId ?? '');
  const [activo, setActivo] = useState(item?.activo ?? true);

  const padre = CATALOGO_PADRE[codigo];

  const guardar = useMutation({
    mutationFn: () => {
      if (item) {
        // El contrato de actualización no mueve el ítem de padre; para eso se
        // retira y se vuelve a crear bajo el correcto.
        return api.put<unknown>(`/catalogos/${item.id}`, {
          nombre: nombre.trim(),
          descripcion: descripcion.trim(),
          activo,
        });
      }
      return api.post<unknown>('/catalogos', {
        catalogo: codigo,
        nombre: nombre.trim(),
        descripcion: descripcion.trim(),
        padreId: padreId === '' ? null : padreId,
      });
    },
    onSuccess: alGuardar,
  });

  const faltaPadre = !item && padre !== undefined && padreId === '';

  return (
    <Dialogo
      titulo={item ? `Editar ${item.nombre}` : `Nuevo valor · ${ETIQUETA_CATALOGO[codigo]}`}
      alCerrar={alCerrar}
    >
      {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}

      <div className="campo">
        <label htmlFor="catalogo-nombre">Nombre</label>
        <input
          id="catalogo-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={200}
          required
        />
      </div>

      {padre ? (
        item ? (
          <div className="campo">
            <span className="campo-titulo">{ETIQUETA_CATALOGO[padre]}</span>
            <p className="campo-nota">
              {item.padreNombre ?? 'Sin asignar'} · no se puede reasignar desde la edición.
            </p>
          </div>
        ) : (
          <SelectorCatalogo
            id="catalogo-padre"
            codigo={padre}
            valor={padreId}
            alCambiar={setPadreId}
            requerido
          />
        )
      ) : null}

      <div className="campo">
        <label htmlFor="catalogo-descripcion">Descripción</label>
        <textarea
          id="catalogo-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={2000}
        />
      </div>

      {item ? (
        <label className="casilla" style={{ marginBottom: 14 }}>
          <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
          <span>Disponible en los desplegables</span>
        </label>
      ) : null}

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={nombre.trim() === '' || faltaPadre}
        etiqueta={item ? 'Guardar cambios' : 'Crear valor'}
      />
    </Dialogo>
  );
}

// ─── Tipos de activo (§5.2) ──────────────────────────────────────────────────

function PanelTiposActivo({ administra }: { administra: boolean }): JSX.Element {
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [editando, setEditando] = useState<TipoActivoOpcion | null | undefined>(undefined);
  const [eliminando, setEliminando] = useState<TipoActivoOpcion | null>(null);

  const { data, isLoading } = useTiposActivo();
  const [busqueda, setBusqueda] = useState('');
  const [soloConCampos, setSoloConCampos] = useState(false);
  const filtrados = (data ?? []).filter(
    (t) =>
      t.nombre.toLowerCase().includes(busqueda.trim().toLowerCase()) && (!soloConCampos || t.campos.length > 0),
  );

  const refrescar = (): Promise<void> => cliente.invalidateQueries({ queryKey: ['tipos-activo'] });

  const eliminar = useMutation({
    mutationFn: (id: string) => api.delete(`/tipos-activo/${id}`),
    onSuccess: async () => {
      await refrescar();
      setEliminando(null);
    },
  });

  return (
    <>
      <section className="panel">
        <div className="panel-cabecera">
          <h2>Tipos de activo</h2>
          {administra ? (
            <button type="button" className="boton pequeno" onClick={() => setEditando(null)}>
              Nuevo tipo
            </button>
          ) : null}
        </div>

        {isLoading ? <Cargando /> : null}
        {data && data.length === 0 ? (
          <Vacio
            titulo="Sin tipos de activo"
            detalle={administra ? 'Agrupan el equipamiento en los reportes por familia.' : undefined}
          />
        ) : null}

        {data && data.length > 0 ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <div className="campo cabecera-lista-buscar">
              <IconoLupa />
              <input
                aria-label="Buscar tipo por nombre"
                placeholder="Buscar por nombre"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <CasillaVerificacion
                marcada={soloConCampos}
                alCambiar={() => setSoloConCampos((v) => !v)}
                etiqueta="Solo con campos"
              />
              <span>Solo con campos</span>
            </div>
          </div>
        ) : null}

        {data && data.length > 0 && filtrados.length === 0 ? (
          <Vacio titulo="Ningún tipo coincide con el filtro" />
        ) : null}

        {data && filtrados.length > 0 ? (
          esMovil ? (
            <div className="tarjetas-ot">
              {filtrados.map((t) => (
                <div key={t.id} className="tarjeta-ot">
                  <div className="tarjeta-ot-cuerpo">
                    <div className="tarjeta-ot-cabecera">
                      <div className="tarjeta-ot-titulo">{t.nombre}</div>
                      <span className="numero-registro">{t.activos} activo(s)</span>
                    </div>
                    <div className="tarjeta-ot-datos">
                      <span>
                        {t.campos.length > 0
                          ? `${t.campos.length} campo(s): ${t.campos.map((c) => CAMPOS_ACTIVO[c.campo as CampoActivo]?.etiqueta ?? c.campo).join(', ')}`
                          : 'Sin campos'}
                      </span>
                    </div>
                    {t.descripcion ? <div className="tarjeta-ot-datos"><span>{t.descripcion}</span></div> : null}
                  </div>
                  {administra ? (
                    <div className="acciones-fila">
                      <button type="button" className="boton secundario pequeno" onClick={() => setEditando(t)}>
                        Editar
                      </button>
                      <button type="button" className="boton secundario pequeno" onClick={() => setEliminando(t)}>
                        Eliminar
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Descripción</th>
                    <th>Activos</th>
                    <th>Campos</th>
                    {administra ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((t) => (
                    <tr key={t.id}>
                      <td style={{ fontWeight: 600 }}>{t.nombre}</td>
                      <td style={{ color: 'var(--texto-medio)' }}>{t.descripcion ?? '—'}</td>
                      <td className="numero-registro">{t.activos}</td>
                      <td
                        className="numero-registro"
                        title={t.campos.map((c) => CAMPOS_ACTIVO[c.campo as CampoActivo]?.etiqueta ?? c.campo).join(', ')}
                      >
                        {t.campos.length > 0 ? t.campos.length : '—'}
                      </td>
                      {administra ? (
                        <td>
                          <div className="acciones-fila">
                            <button type="button" className="boton secundario pequeno" onClick={() => setEditando(t)}>
                              Editar
                            </button>
                            <button type="button" className="boton secundario pequeno" onClick={() => setEliminando(t)}>
                              Eliminar
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </section>

      {editando !== undefined ? (
        <FormularioTipoActivo
          tipo={editando}
          alCerrar={() => setEditando(undefined)}
          alGuardar={async () => {
            await refrescar();
            setEditando(undefined);
          }}
        />
      ) : null}

      {eliminando ? (
        <DialogoConfirmar
          titulo="Eliminar tipo de activo"
          mensaje={`Se eliminará «${eliminando.nombre}», usado por ${eliminando.activos} activo(s).`}
          cargando={eliminar.isPending}
          error={eliminar.error}
          alCerrar={() => {
            eliminar.reset();
            setEliminando(null);
          }}
          alConfirmar={() => eliminar.mutate(eliminando.id)}
        />
      ) : null}
    </>
  );
}

function FormularioTipoActivo({
  tipo,
  alCerrar,
  alGuardar,
}: {
  tipo: TipoActivoOpcion | null;
  alCerrar: () => void;
  alGuardar: () => void | Promise<void>;
}): JSX.Element {
  const [nombre, setNombre] = useState(tipo?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(tipo?.descripcion ?? '');
  const [tiposFallaIds, setTiposFallaIds] = useState<string[]>(tipo?.tiposFalla.map((f) => f.id) ?? []);
  const [camposSel, setCamposSel] = useState<{ campo: CampoActivo; obligatorio: boolean }[]>(
    (tipo?.campos ?? []) as { campo: CampoActivo; obligatorio: boolean }[],
  );

  const { data: tiposFalla } = useTiposFalla();

  function alternarCampo(campo: CampoActivo): void {
    setCamposSel((lista) =>
      lista.some((c) => c.campo === campo)
        ? lista.filter((c) => c.campo !== campo)
        : [...lista, { campo, obligatorio: false }],
    );
  }

  function alternarObligatorio(campo: CampoActivo): void {
    setCamposSel((lista) => lista.map((c) => (c.campo === campo ? { ...c, obligatorio: !c.obligatorio } : c)));
  }

  function alternarFalla(id: string): void {
    setTiposFallaIds((ids) => (ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]));
  }

  // Se elige por familia, no falla por falla (§ pedido): tildar «Aire
  // acondicionado» ya implica todas sus fallas típicas, en vez de obligar a
  // reconocer y tildar cada una suelta en una lista larga. Las que no tienen
  // familia asignada (`familia: null`) no tienen «padre» que las agrupe, así
  // que siguen listadas sueltas, como antes.
  const familias = new Map<string, TipoFallaOpcion[]>();
  const sinFamilia: TipoFallaOpcion[] = [];
  for (const f of tiposFalla ?? []) {
    if (!f.familia) {
      sinFamilia.push(f);
      continue;
    }
    const lista = familias.get(f.familia) ?? [];
    lista.push(f);
    familias.set(f.familia, lista);
  }

  function alternarFamilia(idsFamilia: string[], todasMarcadas: boolean): void {
    setTiposFallaIds((ids) =>
      todasMarcadas ? ids.filter((i) => !idsFamilia.includes(i)) : [...new Set([...ids, ...idsFamilia])],
    );
  }

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = { nombre: nombre.trim(), descripcion: descripcion.trim(), tiposFallaIds, campos: camposSel };
      return tipo
        ? api.put<unknown>(`/tipos-activo/${tipo.id}`, cuerpo)
        : api.post<unknown>('/tipos-activo', cuerpo);
    },
    onSuccess: alGuardar,
  });

  return (
    <Dialogo titulo={tipo ? `Editar ${tipo.nombre}` : 'Nuevo tipo de activo'} ancho={820} alCerrar={alCerrar}>
      {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}

      <div className="campo">
        <label htmlFor="tipo-activo-nombre">Nombre</label>
        <input
          id="tipo-activo-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={150}
          required
          placeholder="Ej.: Equipo láser"
        />
      </div>

      <div className="campo">
        <label htmlFor="tipo-activo-descripcion">Descripción</label>
        <textarea
          id="tipo-activo-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={2000}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, alignItems: 'start' }}>
      <div className="campo">
        <span className="campo-titulo">Campos del activo</span>
        <p className="campo-nota" style={{ marginTop: 0 }}>
          Elija qué datos pide este tipo al registrar un activo, y cuáles son obligatorios.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 4 }}>
          {CODIGOS_CAMPO_ACTIVO.map((codigo) => {
            const seleccion = camposSel.find((c) => c.campo === codigo);
            return (
              <div key={codigo} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  <CasillaVerificacion
                    marcada={!!seleccion}
                    alCambiar={() => alternarCampo(codigo)}
                    etiqueta={CAMPOS_ACTIVO[codigo].etiqueta}
                  />
                  <span>{CAMPOS_ACTIVO[codigo].etiqueta}</span>
                </div>
                {seleccion ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: 160 }}>
                    <CasillaVerificacion
                      marcada={seleccion.obligatorio}
                      alCambiar={() => alternarObligatorio(codigo)}
                      etiqueta={`${CAMPOS_ACTIVO[codigo].etiqueta} obligatorio`}
                    />
                    <span>Obligatorio</span>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <div className="campo">
        <span className="campo-titulo">Fallas típicas</span>
        <p className="campo-nota" style={{ marginTop: 0 }}>
          Al escanear un activo de este tipo, se ofrecerán estas fallas en vez de pedir una descripción
          libre. Se elige por familia: tildar una incluye todas sus fallas.
        </p>
        {(tiposFalla ?? []).length === 0 ? (
          <p className="campo-nota">No hay tipos de falla creados todavía.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
            {[...familias.entries()].map(([nombreFamilia, fallas]) => {
              const idsFamilia = fallas.map((f) => f.id);
              const todasMarcadas = idsFamilia.every((id) => tiposFallaIds.includes(id));
              return (
                <label key={nombreFamilia} className="casilla" title={fallas.map((f) => f.nombre).join(', ')}>
                  <input
                    type="checkbox"
                    checked={todasMarcadas}
                    onChange={() => alternarFamilia(idsFamilia, todasMarcadas)}
                  />
                  <span>
                    {nombreFamilia}
                    <em className="campo-nota"> · {fallas.length} falla(s)</em>
                  </span>
                </label>
              );
            })}
            {sinFamilia.map((f) => (
              <label key={f.id} className="casilla">
                <input type="checkbox" checked={tiposFallaIds.includes(f.id)} onChange={() => alternarFalla(f.id)} />
                <span>{f.nombre}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      </div>

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={nombre.trim() === ''}
        etiqueta={tipo ? 'Guardar cambios' : 'Crear tipo'}
      />
    </Dialogo>
  );
}

/**
 * Tipos de ubicación (§4) — Consultorio, Almacén, Oficina, etc. A diferencia
 * de los tipos de activo, cada uno puede llevar un "checklist base": la
 * plantilla de procedimiento que se sugiere para ese tipo de espacio.
 */
function PanelTiposUbicacion({ administra }: { administra: boolean }): JSX.Element {
  const esMovil = useEsMovil();
  const cliente = useQueryClient();
  const [editando, setEditando] = useState<TipoUbicacionOpcion | null | undefined>(undefined);
  const [eliminando, setEliminando] = useState<TipoUbicacionOpcion | null>(null);

  const { data, isLoading } = useTiposUbicacion();

  const refrescar = (): Promise<void> => cliente.invalidateQueries({ queryKey: ['tipos-ubicacion'] });

  const eliminar = useMutation({
    mutationFn: (id: string) => api.delete(`/tipos-ubicacion/${id}`),
    onSuccess: async () => {
      await refrescar();
      setEliminando(null);
    },
  });

  return (
    <>
      <section className="panel">
        <div className="panel-cabecera">
          <h2>Tipos de ubicación</h2>
          {administra ? (
            <button type="button" className="boton pequeno" onClick={() => setEditando(null)}>
              Nuevo tipo
            </button>
          ) : null}
        </div>

        {isLoading ? <Cargando /> : null}
        {data && data.length === 0 ? (
          <Vacio
            titulo="Sin tipos de ubicación"
            detalle={administra ? 'Agrupan los espacios y les sugieren un checklist base al crear una OT.' : undefined}
          />
        ) : null}

        {data && data.length > 0 ? (
          esMovil ? (
            <div className="tarjetas-ot">
              {data.map((t) => (
                <div key={t.id} className="tarjeta-ot">
                  <div className="tarjeta-ot-cuerpo">
                    <div className="tarjeta-ot-cabecera">
                      <div className="tarjeta-ot-titulo">{t.nombre}</div>
                      <span className="numero-registro">{t.ubicaciones} ubicación(es)</span>
                    </div>
                    <div className="tarjeta-ot-datos">
                      {t.descripcion ? <span>{t.descripcion}</span> : null}
                      {t.checklistBase.length > 0 ? (
                        <Etiqueta tono="azul">{t.checklistBase.length} ítem(s) de checklist</Etiqueta>
                      ) : null}
                    </div>
                  </div>
                  {administra ? (
                    <div className="acciones-fila">
                      <button type="button" className="boton secundario pequeno" onClick={() => setEditando(t)}>
                        Editar
                      </button>
                      <button type="button" className="boton secundario pequeno" onClick={() => setEliminando(t)}>
                        Eliminar
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th>Descripción</th>
                    <th>Checklist base</th>
                    <th>Ubicaciones</th>
                    {administra ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {data.map((t) => (
                    <tr key={t.id}>
                      <td style={{ fontWeight: 600 }}>{t.nombre}</td>
                      <td style={{ color: 'var(--texto-medio)' }}>{t.descripcion ?? '—'}</td>
                      <td>{t.checklistBase.length > 0 ? `${t.checklistBase.length} ítem(s)` : '—'}</td>
                      <td className="numero-registro">{t.ubicaciones}</td>
                      {administra ? (
                        <td>
                          <div className="acciones-fila">
                            <button type="button" className="boton secundario pequeno" onClick={() => setEditando(t)}>
                              Editar
                            </button>
                            <button type="button" className="boton secundario pequeno" onClick={() => setEliminando(t)}>
                              Eliminar
                            </button>
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </section>

      {editando !== undefined ? (
        <FormularioTipoUbicacion
          tipo={editando}
          alCerrar={() => setEditando(undefined)}
          alGuardar={async () => {
            await refrescar();
            setEditando(undefined);
          }}
        />
      ) : null}

      {eliminando ? (
        <DialogoConfirmar
          titulo="Eliminar tipo de ubicación"
          mensaje={`Se eliminará «${eliminando.nombre}», usado por ${eliminando.ubicaciones} ubicación(es).`}
          cargando={eliminar.isPending}
          error={eliminar.error}
          alCerrar={() => {
            eliminar.reset();
            setEliminando(null);
          }}
          alConfirmar={() => eliminar.mutate(eliminando.id)}
        />
      ) : null}
    </>
  );
}

function FormularioTipoUbicacion({
  tipo,
  alCerrar,
  alGuardar,
}: {
  tipo: TipoUbicacionOpcion | null;
  alCerrar: () => void;
  alGuardar: () => void | Promise<void>;
}): JSX.Element {
  const [nombre, setNombre] = useState(tipo?.nombre ?? '');
  const [descripcion, setDescripcion] = useState(tipo?.descripcion ?? '');
  const [checklistBase, setChecklistBase] = useState<string[]>(tipo?.checklistBase.map((i) => i.texto) ?? []);
  const [itemNuevo, setItemNuevo] = useState('');

  function agregarItem(): void {
    const limpio = itemNuevo.trim();
    if (!limpio) return;
    setChecklistBase((items) => [...items, limpio]);
    setItemNuevo('');
  }

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = { nombre: nombre.trim(), descripcion: descripcion.trim(), checklistBase };
      return tipo
        ? api.put<unknown>(`/tipos-ubicacion/${tipo.id}`, cuerpo)
        : api.post<unknown>('/tipos-ubicacion', cuerpo);
    },
    onSuccess: alGuardar,
  });

  return (
    <Dialogo titulo={tipo ? `Editar ${tipo.nombre}` : 'Nuevo tipo de ubicación'} alCerrar={alCerrar}>
      {guardar.isError ? <Aviso tono="error">{mensajeError(guardar.error)}</Aviso> : null}

      <div className="campo">
        <label htmlFor="tipo-ubicacion-nombre">Nombre</label>
        <input
          id="tipo-ubicacion-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={150}
          required
          placeholder="Ej.: Consultorio"
        />
      </div>

      <div className="campo">
        <label htmlFor="tipo-ubicacion-descripcion">Descripción</label>
        <textarea
          id="tipo-ubicacion-descripcion"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          maxLength={2000}
        />
      </div>

      <div className="campo">
        <span className="campo-titulo">Checklist base</span>
        <p className="campo-nota" style={{ marginTop: 0 }}>
          Se copia a cada ubicación nueva de este tipo; después se puede activar/desactivar por ítem en
          cada ubicación, sin afectar al tipo.
        </p>

        {checklistBase.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
            {checklistBase.map((item, i) => (
              <div key={i} className="fila-archivo">
                <span style={{ flex: 1 }}>{item}</span>
                <button
                  type="button"
                  className="fila-archivo-quitar"
                  aria-label={`Quitar ${item}`}
                  onClick={() => setChecklistBase((items) => items.filter((_, idx) => idx !== i))}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={itemNuevo}
            onChange={(e) => setItemNuevo(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                agregarItem();
              }
            }}
            maxLength={300}
            placeholder="Ej.: Extintor vigente"
            style={{ flex: 1 }}
          />
          <button type="button" className="boton secundario pequeno" onClick={agregarItem} disabled={!itemNuevo.trim()}>
            Agregar
          </button>
        </div>
      </div>

      <AccionesDialogo
        alCerrar={alCerrar}
        alConfirmar={() => guardar.mutate()}
        cargando={guardar.isPending}
        deshabilitado={nombre.trim() === ''}
        etiqueta={tipo ? 'Guardar cambios' : 'Crear tipo'}
      />
    </Dialogo>
  );
}

// ─── Grupos de activos ───────────────────────────────────────────────────────

interface GrupoActivoCatalogo {
  id: string;
  nombre: string;
  tipos: { id: string; nombre: string }[];
}

/**
 * Grupos de activos: cada grupo reúne tipos de activo y es una hoja del Excel de activos.
 * Un tipo solo puede estar en un grupo.
 */
function PanelGruposActivo({ administra }: { administra: boolean }): JSX.Element {
  const cliente = useQueryClient();
  const [seleccionadoId, setSeleccionadoId] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState('');

  const { data: grupos, isLoading } = useQuery({
    queryKey: ['grupos-activo'],
    queryFn: () => api.get<GrupoActivoCatalogo[]>('/grupos-activo'),
  });
  const seleccionado = grupos?.find((g) => g.id === seleccionadoId) ?? null;

  const refrescar = async (): Promise<void> => {
    await cliente.invalidateQueries({ queryKey: ['grupos-activo'] });
  };

  const crear = useMutation({
    mutationFn: (nombre: string) => api.post<GrupoActivoCatalogo>('/grupos-activo', { nombre, tiposIds: [] }),
    onSuccess: async (creado) => {
      await refrescar();
      setSeleccionadoId(creado.id);
      setNuevo('');
    },
  });

  return (
    <div className="panel">
      <div className="panel-cabecera">
        <h2>Grupos de activos</h2>
      </div>
      <div className="panel-cuerpo" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <p className="campo-nota">
          Cree un grupo y marque qué tipos de activo le pertenecen. Cada grupo sale en su propia hoja del Excel de
          activos. Un tipo solo puede estar en un grupo.
        </p>

        {administra ? (
          <div className="fila-crear-grupo" style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="campo" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
              <label htmlFor="grupo-nuevo">Nuevo grupo</label>
              <input
                id="grupo-nuevo"
                value={nuevo}
                onChange={(e) => setNuevo(e.target.value)}
                maxLength={120}
                placeholder="Ej.: Equipos clínicos"
              />
            </div>
            <button
              type="button"
              className="boton"
              disabled={nuevo.trim() === '' || crear.isPending}
              onClick={() => crear.mutate(nuevo.trim())}
            >
              {crear.isPending ? 'Creando…' : 'Crear grupo'}
            </button>
          </div>
        ) : null}
        <ErroresFormulario error={crear.error} />

        {isLoading ? <Cargando /> : null}
        {grupos && grupos.length === 0 ? (
          <Vacio titulo="Todavía no hay grupos" detalle={administra ? 'Cree el primero arriba.' : undefined} />
        ) : null}
        {grupos && grupos.length > 0 ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {grupos.map((g) => (
              <button
                key={g.id}
                type="button"
                className={`boton${g.id === seleccionadoId ? '' : ' secundario'} pequeno`}
                aria-pressed={g.id === seleccionadoId}
                onClick={() => setSeleccionadoId(g.id)}
              >
                {g.nombre} · {g.tipos.length} {g.tipos.length === 1 ? 'tipo' : 'tipos'}
              </button>
            ))}
          </div>
        ) : null}

        {seleccionado ? (
          <EditorGrupoActivo
            key={seleccionado.id}
            grupo={seleccionado}
            grupos={grupos ?? []}
            administra={administra}
            alGuardar={refrescar}
            alEliminar={async () => {
              await refrescar();
              setSeleccionadoId(null);
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function EditorGrupoActivo({
  grupo,
  grupos,
  administra,
  alGuardar,
  alEliminar,
}: {
  grupo: GrupoActivoCatalogo;
  grupos: GrupoActivoCatalogo[];
  administra: boolean;
  alGuardar: () => Promise<void>;
  alEliminar: () => Promise<void>;
}): JSX.Element {
  const [eliminando, setEliminando] = useState(false);
  const { data: tipos, isLoading } = useTiposActivo();
  const [nombre, setNombre] = useState(grupo.nombre);
  const [tiposIds, setTiposIds] = useState<string[]>(grupo.tipos.map((t) => t.id));

  // Grupo actual de cada tipo, para avisar cuando se mueve desde otro grupo.
  const grupoDeTipo = new Map<string, string>();
  for (const g of grupos) {
    if (g.id === grupo.id) continue;
    for (const t of g.tipos) grupoDeTipo.set(t.id, g.nombre);
  }

  const guardar = useMutation({
    mutationFn: () => api.put(`/grupos-activo/${grupo.id}`, { nombre: nombre.trim(), tiposIds }),
    onSuccess: () => alGuardar(),
  });

  const eliminar = useMutation({
    mutationFn: () => api.delete(`/grupos-activo/${grupo.id}`),
    onSuccess: async () => {
      setEliminando(false);
      await alEliminar();
    },
  });

  const actuales = grupo.tipos.map((t) => t.id);
  const cambios =
    nombre.trim() !== grupo.nombre ||
    tiposIds.length !== actuales.length ||
    tiposIds.some((id) => !actuales.includes(id));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, borderTop: '1px solid var(--borde)', paddingTop: 12 }}>
      <div className="campo">
        <label htmlFor="grupo-nombre">Nombre</label>
        <input
          id="grupo-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          maxLength={120}
          disabled={!administra}
        />
      </div>

      {isLoading ? <Cargando /> : null}
      {tipos ? (
        <SeleccionMultiple
          etiqueta="Tipos de activo de este grupo"
          opciones={tipos.map((t) => ({
            id: t.id,
            nombre: t.nombre,
            nota: grupoDeTipo.get(t.id) ? `Hoy en ${grupoDeTipo.get(t.id)}` : undefined,
          }))}
          seleccion={tiposIds}
          alCambiar={setTiposIds}
          soloLectura={!administra}
          vacio="No hay tipos de activo creados."
        />
      ) : null}

      <ErroresFormulario error={guardar.error} />
      {administra ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="boton secundario" onClick={() => setEliminando(true)}>
            Eliminar grupo
          </button>
          <button
            type="button"
            className="boton"
            disabled={!cambios || nombre.trim() === '' || guardar.isPending}
            onClick={() => guardar.mutate()}
          >
            {guardar.isPending ? 'Guardando…' : 'Guardar grupo'}
          </button>
        </div>
      ) : null}

      {eliminando ? (
        <DialogoConfirmar
          titulo="Eliminar grupo"
          etiqueta="Eliminar"
          mensaje={`«${grupo.nombre}» dejará de aparecer y sus tipos de activo quedarán sin grupo. Los activos no se modifican.`}
          cargando={eliminar.isPending}
          error={eliminar.error}
          alCerrar={() => {
            eliminar.reset();
            setEliminando(false);
          }}
          alConfirmar={() => eliminar.mutate()}
        />
      ) : null}
    </div>
  );
}
